import { useMemo, useState } from 'react';
import { DrawerCalendar } from './FilterDrawer.styles';

export interface DateRangeFieldProps {
    wrapId: string;
    value: (Date | null)[] | null;
    onChange: (value: (Date | null)[] | null) => void;
}

// Anchor the two-month view so the right panel is the current month and the
// left panel is the previous month — PrimeReact renders viewDate's month first.
function initialViewDate(): Date {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth() - 1, 1);
}

function startOfDay(date: Date): Date {
    const snapped = new Date(date);
    snapped.setHours(0, 0, 0, 0);
    return snapped;
}

function endOfDay(date: Date): Date {
    const snapped = new Date(date);
    snapped.setHours(23, 59, 59, 999);
    return snapped;
}

/**
 * Snap the Calendar's range value to local-day boundaries. Range-mode onChange
 * fires `[Date, null]` on the first click (half-selected) and `[Date, Date]`
 * on the second — both shapes pass through here. Exported so the normalization
 * can be unit-tested without driving the Calendar through jsdom.
 */
export function snapRange(next: (Date | null)[] | null): (Date | null)[] | null {
    if (!next) return null;
    return [
        next[0] ? startOfDay(next[0]) : null,
        next[1] ? endOfDay(next[1]) : null,
    ];
}

export function DateRangeField({ wrapId, value, onChange }: DateRangeFieldProps) {
    const [viewDate, setViewDate] = useState<Date>(initialViewDate);
    // Hold maxDate stable for the component's lifetime — a fresh Date on every
    // render would churn PrimeReact's internal change detection.
    const maxDate = useMemo(() => new Date(), []);

    return (
        <DrawerCalendar
            inputId={wrapId}
            data-testid={wrapId}
            value={value}
            onChange={(e) => onChange(snapRange(Array.isArray(e.value) ? e.value : null))}
            viewDate={viewDate}
            onViewDateChange={(e) => setViewDate(e.value)}
            selectionMode="range"
            readOnlyInput
            showIcon
            numberOfMonths={2}
            maxDate={maxDate}
        />
    );
}
